import { Component } from '@theme/component';
import { ThemeEvents, CustomizerOpenEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} [productImage] - Product image, absent on a
 *   product with no image. The bar hides it (the pill spans the whole bar) and
 *   only its `src` is read, for the fly-to-cart background.
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const buyButtonsBlock = this.#getScrollAnchor();
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.boundingClientRect;
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  // Public action handlers
  /**
   * Opens the customizer. Bound instead of handleAddToCartClick on a customizer
   * product, where the bar reads "Start Customizing" and nothing goes into the
   * cart until the designer has produced a `_config`. The overlay listens on the
   * document bus and matches on config id, exactly as the in-page trigger does.
   */
  openCustomizer = () => {
    this.dispatchEvent(new CustomizerOpenEvent(this.dataset.productId ?? ''));
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const targetButton = this.#targetAddToCartButton;
    if (!targetButton) return;

    // Listen before clicking, so a fast response can't land first.
    const settled = this.#cartAddSettled();

    // Nothing is going into the cart, and the browser has already said so: don't
    // celebrate, and don't scroll on top of it.
    if (!this.#puppetClick(targetButton)) return;

    const celebration = this.#celebrateAdd();

    // The server can still refuse the add (out of stock, over the quantity maximum).
    // Take the celebration back and put the message on screen.
    if ((await settled) === 'rejected') {
      this.#cancelCelebration();
      this.#revealFormFeedback();
      return;
    }

    await celebration;
  };

  /**
   * Clicks the real add-to-cart button and reports whether an add actually
   * started.
   *
   * The real button is a form submitter, so clicking it runs the browser's own
   * constraint validation, and `submit` fires only once that passes. Both happen
   * synchronously inside `.click()`, so an add is known to be in flight before any
   * network work and the pill can react at once.
   *
   * A blocked submit is the name-jewelry case, an empty required engraving field.
   * The browser focuses the offending input itself and never fires `submit`.
   *
   * `data-puppet` tells the real button's handler (capture phase, product-form.js) to
   * skip its animation. It is cleared as soon as `.click()` returns: waiting for the
   * cart's reply would leave it stuck when validation blocks the submit.
   *
   * @param {HTMLButtonElement} targetButton - The buy-buttons add-to-cart button.
   * @returns {boolean} Whether the form submitted.
   */
  #puppetClick(targetButton) {
    const form = targetButton.closest('form');
    let submitted = false;
    const onSubmit = () => {
      submitted = true;
    };

    form?.addEventListener('submit', onSubmit, { capture: true });
    try {
      targetButton.dataset.puppet = 'true';
      targetButton.click();
    } finally {
      targetButton.dataset.puppet = 'false';
      form?.removeEventListener('submit', onSubmit, { capture: true });
    }

    return submitted;
  }

  /**
   * Plays the pill's confirmation: the burst slides over the label and a tile of
   * the product image flies to the cart icon.
   *
   * Optimistic, on submit rather than on the cart's reply, to match the main
   * add-to-cart button (product-form.js `animateAddToCart`). Waiting for `cart:update`
   * means two round trips, a dead pause of a second or two after the press.
   */
  async #celebrateAdd() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const productImageSrc = this.refs.productImage?.src;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.refs.addToCartButton.dataset.added = 'true';

    // The bar hides the thumbnail, which then measures as a zero rect, so fly from the
    // button at the default 40px size with the product image as the tile's artwork.
    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    if (cartIcon && productImageSrc) {
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImageSrc})`);
      flyToCartElement.source = this.refs.addToCartButton;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
    }

    // onAnimationEnd reads getAnimations(), which flushes style, so yield first to keep that
    // off the press's critical path (as product-form.js animateAddToCart does).
    await yieldToMainThread();

    // Always await the burst and schedule the reset: bailing out early (no cart icon or
    // image) would strand the pill showing nothing but a checkmark.
    const animated = flyToCartElement.isConnected
      ? [this.refs.addToCartButton, flyToCartElement]
      : [this.refs.addToCartButton];
    await onAnimationEnd(animated);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Puts the pill back to its resting label after a refused add.
   */
  #cancelCelebration() {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.refs.addToCartButton.removeAttribute('data-added');
  }

  /**
   * Scrolls the buy-buttons block back into view. Its error message is the only
   * place a rejected add is shown or announced.
   */
  #revealFormFeedback() {
    // product-form.js dispatches the error event before it un-hides the message,
    // so target the form rather than the (still hidden) error span.
    this.#getProductForm()?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Watches for the puppeted form to report the outcome of an add.
   *
   * Runs alongside the celebration only to catch a refusal and take it back. The
   * deadline is long on purpose: the add is two round trips, and a short one would
   * revert a slow success. A reply that never arrives is `unknown` and changes nothing.
   *
   * Only this product form's own events count: `cart:update` fires for every cart
   * mutation on the page. Both event types bubble from the `product-form-component`,
   * so containment identifies the source.
   *
   * @returns {Promise<'added' | 'rejected' | 'unknown'>} What the form reported.
   */
  #cartAddSettled() {
    return new Promise((resolve) => {
      const controller = new AbortController();
      const productForm = this.#getProductForm();
      /** @param {'added' | 'rejected' | 'unknown'} outcome */
      const settle = (outcome) => {
        clearTimeout(timeout);
        controller.abort();
        resolve(outcome);
      };
      /** @param {Event} event */
      const isOwnAdd = (event) =>
        Boolean(productForm) && event.target instanceof Node && productForm?.contains(event.target);

      const timeout = setTimeout(() => settle('unknown'), 30000);
      const options = { signal: controller.signal };

      // Also give up if the component goes away mid-flight.
      const giveUp = () => settle('unknown');
      this.#abortController.signal.addEventListener('abort', giveUp, { ...options, once: true });
      document.addEventListener(
        ThemeEvents.cartUpdate,
        (event) => {
          if (!isOwnAdd(event)) return;
          const didError = /** @type {CustomEvent} */ (event).detail?.data?.didError === true;
          settle(didError ? 'rejected' : 'added');
        },
        options
      );
      document.addEventListener(
        ThemeEvents.cartError,
        (event) => {
          if (isOwnAdd(event)) settle('rejected');
        },
        options
      );
    });
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    // A wizard-owned axis stays rendered and checked but hidden, on a value the shopper never
    // chose, so naming it here invents a combination no variant has.
    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .filter((option) => !option.closest('.variant-option--wizard-owned'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * The element the bar tracks: once it scrolls out of view above, the bar shows.
   *
   * The trigger is checked first: it only renders for a customizer product, where
   * the buy-buttons block is a near-zero-height shell that would misplace the bar's
   * hand-off. Everywhere else this falls through to the buy-buttons block.
   *
   * @returns {Element | null} The element to observe, or null if neither is present.
   */
  #getScrollAnchor() {
    const trigger = this.closest('.shopify-section')?.querySelector('custom-customizer-trigger');
    return trigger ?? this.#getProductForm()?.closest('.buy-buttons-block') ?? null;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
